import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ChecklistItem } from '@/types/onboarding';

/**
 * Clé localStorage pour la checklist
 */
const CHECKLIST_STORAGE_KEY = 'b8ild_onboarding_checklist_v2';

/**
 * Clé localStorage pour le tips simulation retard
 */
const TIPS_RETARD_SEEN_KEY = 'b8ild_tips_retard_seen';

/**
 * Clé localStorage pour le clic sur "Répartir mon budget"
 */
const BUDGET_VENTILE_CLICKED_KEY = 'b8ild_budget_ventile_clicked';

/**
 * Clé localStorage pour persister les étapes complétées
 * Permet de garder une étape complétée même si les données sont supprimées
 */
const STEPS_COMPLETED_KEY = 'b8ild_checklist_steps_completed';

/**
 * Items de la checklist (9 étapes)
 */
const CHECKLIST_ITEMS: Omit<ChecklistItem, 'isCompleted'>[] = [
  {
    id: 'equipe',
    label: 'Créer toutes vos équipes',
    description: 'Utilisez l\'import groupé pour aller plus vite',
    action: '/team',
    icon: '👥',
  },
  {
    id: 'chantier',
    label: 'Créer votre premier chantier',
    description: 'Nom, client, dates, adresse et référence',
    action: '/projects',
    icon: '🏗️',
  },
  {
    id: 'devis',
    label: 'Ajouter votre/vos devis',
    description: 'Utilisez la fonction Scan IA',
    action: '/projects',
    icon: '📄',
  },
  {
    id: 'budget-ventile',
    label: 'Définir un budget par poste',
    description: 'Main d\'œuvre, matériaux, sous-traitance...',
    action: '/projects?tab=analyse&subtab=budget&showBudgetTip=true',
    icon: '📊',
  },
  {
    id: 'affectation',
    label: "Affecter l'équipe au chantier",
    description: 'Assignez les membres',
    action: '/projects',
    icon: '🔗',
  },
  {
    id: 'factures',
    label: 'Ajouter les factures fournisseurs',
    description: 'Matériaux et fournitures',
    action: '/projects',
    icon: '🧾',
  },
  {
    id: 'couts-annexes',
    label: 'Ajouter les coûts annexes',
    description: 'Sous-traitance, location...',
    action: '/projects',
    icon: '💰',
  },
  {
    id: 'paiement',
    label: 'Renseigner le paiement',
    description: 'Statut et acomptes',
    action: '/projects',
    icon: '💳',
  },
  {
    id: 'simulation-retard',
    label: 'Simuler un retard',
    description: 'Anticipez l\'impact financier',
    action: '/financial?tab=penalty-simulator',
    icon: '⏱️',
  },
  {
    id: 'analyse-ia',
    label: "Lancer l'analyse IA",
    description: 'Obtenez des recommandations',
    action: '/projects',
    icon: '🤖',
  },
];

/**
 * Hook pour gérer la checklist d'onboarding sur le dashboard
 * Vérifie les conditions en temps réel depuis Supabase
 */
export function useOnboardingChecklist(entrepriseId?: string) {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [isVisible, setIsVisible] = useState(true);
  const [userDismissed, setUserDismissed] = useState(false); // User a cliqué "Je connais déjà B8ild"
  const [showAsGuide, setShowAsGuide] = useState(false); // Forcer l'affichage en mode Guide
  const [loading, setLoading] = useState(true);

  /**
   * Charge l'état de visibilité depuis localStorage
   */
  const loadVisibilityState = useCallback(() => {
    try {
      const savedState = localStorage.getItem(CHECKLIST_STORAGE_KEY);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        setIsVisible(parsed.isVisible !== false);
        setUserDismissed(parsed.userDismissed === true);
        setShowAsGuide(parsed.showAsGuide === true);
      }
    } catch (e) {
      setIsVisible(true);
      setUserDismissed(false);
      setShowAsGuide(false);
    }
  }, []);

  /**
   * Vérifie les conditions de chaque étape depuis Supabase
   * IMPORTANT: Une étape déjà complétée en localStorage reste complétée DÉFINITIVEMENT
   * même si les données sont supprimées (seul resetChecklist efface cet état)
   */
  const checkConditions = useCallback(async () => {
    // ÉTAPE 1: Charger les étapes déjà complétées depuis localStorage AVANT TOUT
    // Ces étapes restent complétées QUOI QU'IL ARRIVE (même erreur DB, même suppression données)
    let savedSteps: Record<string, boolean> = {};
    try {
      const saved = localStorage.getItem(STEPS_COMPLETED_KEY);
      if (saved) {
        savedSteps = JSON.parse(saved);
      }
    } catch (e) {
      console.warn('[useOnboardingChecklist] Erreur lecture localStorage:', e);
    }

    // Pour simulation-retard : toujours vérifier localStorage (indépendant de la DB)
    const simulationRetardSeen = localStorage.getItem(TIPS_RETARD_SEEN_KEY) === 'true';
    if (simulationRetardSeen && !savedSteps['simulation-retard']) {
      savedSteps['simulation-retard'] = true;
    }

    // Pour budget-ventile : validé par clic sur le bouton (localStorage)
    const budgetVentileClicked = localStorage.getItem(BUDGET_VENTILE_CLICKED_KEY) === 'true';
    if (budgetVentileClicked && !savedSteps['budget-ventile']) {
      savedSteps['budget-ventile'] = true;
    }

    if (!entrepriseId) {
      // Si pas d'entrepriseId, utiliser UNIQUEMENT les étapes sauvegardées en localStorage
      setItems(CHECKLIST_ITEMS.map(item => ({
        ...item,
        isCompleted: savedSteps[item.id] === true,
      })));
      setLoading(false);
      return;
    }

    try {
      // Requêtes parallèles pour vérifier chaque condition
      const [
        membresResult,
        chantiersResult,
        devisResult,
        affectationsResult,
        facturesResult,
        fraisResult,
        budgetVentileResult,
        paiementsResult,
        analysesResult,
      ] = await Promise.all([
        // 1. Équipe : au moins 1 membre
        supabase
          .from('membres_equipe')
          .select('id', { count: 'exact', head: true })
          .eq('entreprise_id', entrepriseId),

        // 2. Chantier : au moins 1 chantier
        supabase
          .from('chantiers')
          .select('id', { count: 'exact', head: true })
          .eq('entreprise_id', entrepriseId),

        // 3. Devis : au moins 1 devis
        supabase
          .from('devis')
          .select('id, chantiers!inner(entreprise_id)', { count: 'exact', head: true })
          .eq('chantiers.entreprise_id', entrepriseId),

        // 4. Affectation : au moins 1 affectation
        supabase
          .from('affectations_chantiers')
          .select('id, chantiers!inner(entreprise_id)', { count: 'exact', head: true })
          .eq('chantiers.entreprise_id', entrepriseId),

        // 5. Factures fournisseurs : au moins 1 facture
        supabase
          .from('factures_fournisseurs')
          .select('id, chantiers!inner(entreprise_id)', { count: 'exact', head: true })
          .eq('chantiers.entreprise_id', entrepriseId),

        // 6. Frais/coûts annexes : au moins 1 frais
        supabase
          .from('frais_chantier')
          .select('id', { count: 'exact', head: true })
          .eq('entreprise_id', entrepriseId),

        // 7. Budget ventilé : au moins 1 chantier avec budget_ventile configuré
        supabase
          .from('chantiers')
          .select('id', { count: 'exact', head: true })
          .eq('entreprise_id', entrepriseId)
          .not('budget_ventile', 'is', null),

        // 8. Paiement : au moins 1 paiement client enregistré
        supabase
          .from('paiements_clients')
          .select('id, chantiers!inner(entreprise_id)', { count: 'exact', head: true })
          .eq('chantiers.entreprise_id', entrepriseId),

        // 9. Analyse IA : au moins 1 analyse
        supabase
          .from('analyses_ia')
          .select('id, chantiers!inner(entreprise_id)', { count: 'exact', head: true })
          .eq('chantiers.entreprise_id', entrepriseId),
      ]);

      // Conditions actuelles depuis la DB (utilisées SEULEMENT pour détecter de nouvelles complétions)
      const dbConditions: Record<string, boolean> = {
        'equipe': (membresResult.count || 0) >= 1,
        'chantier': (chantiersResult.count || 0) >= 1,
        'devis': (devisResult.count || 0) >= 1,
        'affectation': (affectationsResult.count || 0) >= 1,
        'factures': (facturesResult.count || 0) >= 1,
        'couts-annexes': (fraisResult.count || 0) >= 1,
        'budget-ventile': budgetVentileClicked, // Validé par clic, pas par DB
        'paiement': (paiementsResult.count || 0) >= 1,
        'simulation-retard': simulationRetardSeen,
        'analyse-ia': (analysesResult.count || 0) >= 1,
      };

      // LOGIQUE DE FUSION CORRIGÉE:
      // - Si localStorage dit TRUE → reste TRUE (JAMAIS remis à false)
      // - Si localStorage dit FALSE/undefined ET DB dit TRUE → passe à TRUE et sauvegarde
      // - Si localStorage dit FALSE/undefined ET DB dit FALSE → reste FALSE
      let hasNewCompletion = false;
      const finalConditions: Record<string, boolean> = {};

      for (const stepId of Object.keys(dbConditions)) {
        // PRIORITÉ ABSOLUE: Si déjà complété en localStorage → reste TRUE DÉFINITIVEMENT
        if (savedSteps[stepId] === true) {
          finalConditions[stepId] = true;
        } else if (dbConditions[stepId]) {
          // Nouvelle complétion détectée → sauvegarder en localStorage
          finalConditions[stepId] = true;
          savedSteps[stepId] = true;
          hasNewCompletion = true;
        } else {
          // Pas encore complété
          finalConditions[stepId] = false;
        }
      }

      // Sauvegarder si nouvelles complétions
      if (hasNewCompletion) {
        localStorage.setItem(STEPS_COMPLETED_KEY, JSON.stringify(savedSteps));
      }

      // Mettre à jour les items avec leur statut de complétion
      const updatedItems: ChecklistItem[] = CHECKLIST_ITEMS.map(item => ({
        ...item,
        isCompleted: finalConditions[item.id] || false,
      }));

      setItems(updatedItems);
    } catch (error) {
      console.error('[useOnboardingChecklist] Erreur vérification conditions:', error);
      // EN CAS D'ERREUR: Utiliser les étapes sauvegardées en localStorage (pas de reset à false!)
      setItems(CHECKLIST_ITEMS.map(item => ({
        ...item,
        isCompleted: savedSteps[item.id] === true,
      })));
    } finally {
      setLoading(false);
    }
  }, [entrepriseId]);

  // Charger l'état de visibilité au montage
  useEffect(() => {
    loadVisibilityState();
  }, [loadVisibilityState]);

  // Vérifier les conditions quand entrepriseId change
  useEffect(() => {
    checkConditions();
  }, [checkConditions]);

  // Rafraîchir périodiquement (toutes les 30 secondes)
  useEffect(() => {
    if (!entrepriseId) return;

    const interval = setInterval(() => {
      checkConditions();
    }, 30000);

    return () => clearInterval(interval);
  }, [entrepriseId, checkConditions]);

  // Écouter les changements localStorage pour rafraîchir immédiatement (cross-tab)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === TIPS_RETARD_SEEN_KEY || e.key === BUDGET_VENTILE_CLICKED_KEY) {
        checkConditions();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkConditions]);

  // Écouter l'événement personnalisé pour rafraîchir (même onglet)
  useEffect(() => {
    const handleChecklistUpdate = () => {
      checkConditions();
    };

    window.addEventListener('b8ild-checklist-update', handleChecklistUpdate);
    return () => window.removeEventListener('b8ild-checklist-update', handleChecklistUpdate);
  }, [checkConditions]);

  /**
   * Masque la checklist et passe en mode Guide (localStorage)
   * Si la checklist n'est pas complétée, on force le mode Guide
   */
  const hideChecklist = useCallback((forceGuideMode = true) => {
    // Ne pas masquer complètement, passer en mode Guide à la place
    setUserDismissed(true);
    if (forceGuideMode) {
      setShowAsGuide(true);
    }
    localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify({
      isVisible: true, // Reste visible mais en mode Guide
      userDismissed: true,
      showAsGuide: forceGuideMode
    }));
  }, []);

  /**
   * Masque complètement le panneau (Guide inclus)
   */
  const hideCompletely = useCallback(() => {
    setIsVisible(false);
    localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify({
      isVisible: false,
      userDismissed: true,
      showAsGuide: false
    }));
  }, []);

  /**
   * Affiche la checklist (depuis Paramètres)
   */
  const showChecklist = useCallback(() => {
    setIsVisible(true);
    setUserDismissed(false);
    setShowAsGuide(false);
    localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify({
      isVisible: true,
      userDismissed: false,
      showAsGuide: false
    }));
  }, []);

  /**
   * Réinitialise la checklist (y compris la simulation de retard et les étapes persistées)
   * C'est la SEULE façon de remettre les étapes à zéro
   */
  const resetChecklist = useCallback(() => {
    localStorage.removeItem(CHECKLIST_STORAGE_KEY);
    localStorage.removeItem(TIPS_RETARD_SEEN_KEY);
    localStorage.removeItem(STEPS_COMPLETED_KEY); // Efface les étapes persistées
    setIsVisible(true);
    setUserDismissed(false);
    setShowAsGuide(false);
    checkConditions();
  }, [checkConditions]);

  /**
   * Force le rafraîchissement des conditions
   */
  const refresh = useCallback(() => {
    checkConditions();
  }, [checkConditions]);

  // Calculs
  const completedCount = items.filter(i => i.isCompleted).length;
  const totalCount = items.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const isAllCompleted = completedCount === totalCount && totalCount > 0;

  // Afficher en mode Guide si : userDismissed=true OU isAllCompleted=true
  const shouldShowAsGuide = showAsGuide || userDismissed || isAllCompleted;

  return {
    items,
    isVisible,
    loading,
    completedCount,
    totalCount,
    progressPercent,
    isAllCompleted,
    userDismissed,
    showAsGuide: shouldShowAsGuide, // Mode Guide forcé
    hideChecklist,
    hideCompletely,
    showChecklist,
    resetChecklist,
    refresh,
    // Ancienne fonction pour compatibilité (ne fait rien car auto-détecté)
    completeItem: () => {},
  };
}
